/**
 * Pieces the FM ticket screens share (FM Manager rebuild W2, C2–C7): the
 * response shapes of GET /fm/tickets and /fm/tickets/{id}, the pills in the
 * prototype's wording, the pop-up sheet, and the write plumbing (refresh,
 * offline, error wording).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Dialog } from '@headlessui/react';
import { useQueryClient } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { duration } from '@/utils/fmTime';
import { routeRequestError } from '@/utils/validation';
import type { FmDisplayState } from '@/utils/fmTicketState';

// ---------------------------------------------------------------------------
// Shapes (backend/routers/fm_tickets.py)
// ---------------------------------------------------------------------------

export interface FmSla {
  outcome: string; paused: boolean; final: boolean;
  remaining_minutes: number | null; overrun_minutes: number | null;
  took_minutes: number | null;
  resolution_target_minutes: number | null; response_target_minutes: number | null;
}

export interface FmTicketRow {
  id: string; number: string | null; title: string;
  priority: 'P1' | 'P2' | 'P3' | 'P4' | null;
  category: string | null; category_label: string | null;
  where: string | null; site: { id: string | null; name: string } | null;
  timezone: string | null;
  state: FmDisplayState; sla: FmSla | null;
  repeat_count: number; escalation_level: number;
  channel: string; created_at: string; tint: 'bad' | 'warn' | null;
}

export type FmTab = 'open' | 'breached' | 'p1' | 'hold' | 'resolved' | 'closed' | 'all' | 'no_site';

export interface FmTicketList {
  tab: FmTab; counts: Partial<Record<FmTab, number>>; rows: FmTicketRow[]; truncated: boolean;
}

export interface FmTicketDetail extends FmTicketRow {
  site_full: string | null;
  description: string | null;
  targets: {
    label: string | null; respond_minutes: number | null; fix_minutes: number | null;
    update_minutes: number | null; round_the_clock: boolean; from_contract: boolean;
  };
  penalty_applies: boolean;
  hold: { reason: string | null; paused_minutes: number } | null;
  next_update_due_at: string | null;
  details: {
    asset: string | null; requester: string | null; assignee: string | null;
    assignee_is_vendor: boolean; photos_before: number; photos_after: number;
  };
  verdict: string | null;
  resolved_at: string | null;
  escalation: { level: number; matrix: boolean;
                next: { level: number; role: string | null; name: string | null } | null };
  contract: { id: string; name: string } | null;
  timeline: { at: string; text: string; by: string | null }[];
  whatsapp_phone_known: boolean;
  can: { hold: boolean; resume: boolean; reassign: boolean; escalate: boolean; resolve: boolean;
         verdict: boolean; whatsapp: boolean; set_site: boolean; update_caller: boolean };
  /** Logged by the FM for a caller (phone / WhatsApp): updates go to the caller. */
  logged_for_caller: boolean;
}

export interface FmInboxRow { id: string; from: string; phone: string; text: string; received_at: string }

// ---------------------------------------------------------------------------
// Pills, in the prototype's words
// ---------------------------------------------------------------------------

export const PILL = {
  red: 'bg-danger-50 text-danger-700',
  amber: 'bg-warning-50 text-warning-800',
  green: 'bg-success-50 text-success-700',
  blue: 'bg-info-50 text-info-700',
  plain: 'bg-surface-muted text-content-secondary',
} as const;

/** The prototype's slaText: [words, colour]. */
export function slaText(sla: FmSla | null): [string, keyof typeof PILL] {
  if (!sla) return ['No SLA', 'plain'];
  if (sla.final) {
    if (sla.outcome === 'unmeasurable') return ['No SLA', 'plain'];
    const took = sla.took_minutes != null ? ` (${duration(sla.took_minutes)})` : '';
    return sla.outcome === 'breached' ? [`Closed late${took}`, 'red'] : [`Within SLA${took}`, 'green'];
  }
  if (sla.paused) {
    const left = sla.remaining_minutes != null ? ` · ${duration(Math.max(0, sla.remaining_minutes))} left` : '';
    return [`⏸ Clock paused${left}`, 'blue'];
  }
  if (sla.outcome === 'breached') return [`Breached ${duration(sla.overrun_minutes ?? 0)} ago`, 'red'];
  if (sla.remaining_minutes != null) {
    return [`${duration(sla.remaining_minutes)} left`, sla.remaining_minutes < 60 ? 'amber' : 'plain'];
  }
  return ['No SLA', 'plain'];
}

export function SlaPill({ sla }: { sla: FmSla | null }) {
  const [text, tone] = slaText(sla);
  return <span data-testid="sla-pill" className={`badge whitespace-nowrap ${PILL[tone]}`}>{text}</span>;
}

export function PriorityBadge({ p }: { p: string | null }) {
  if (!p) return null;
  const tone = p === 'P1' ? PILL.red : p === 'P2' ? PILL.amber : PILL.plain;
  return <span className={`badge ${tone}`}>{p}</span>;
}

export function stateTone(key: string): keyof typeof PILL {
  return key === 'on_hold' ? 'blue' : key === 'reopened' ? 'red' : key === 'closed' ? 'green' : 'plain';
}

export function StatePill({ state }: { state: FmDisplayState }) {
  return <span data-testid="state-pill" className={`badge ${PILL[stateTone(state.key)]}`}>{state.label}</span>;
}

export function RepeatEscPills({ repeat, level }: { repeat: number; level: number }) {
  return (
    <>
      {repeat > 0 && <span data-testid="repeat-pill" className={`badge ${PILL.amber}`}>Repeat ×{repeat}</span>}
      {level > 0 && <span data-testid="level-pill" className={`badge ${PILL.red}`}>L{level}</span>}
    </>
  );
}

// ---------------------------------------------------------------------------
// The sheet: centred on a desk, full screen on a phone
// ---------------------------------------------------------------------------

export function FmSheet({ open, onClose, title, subtitle, children, wide, testid, tech = false }: {
  open: boolean; onClose: () => void; title: ReactNode; subtitle?: ReactNode;
  children: ReactNode; wide?: boolean; testid?: string;
  /** Opened from a technician screen: the whole panel — its Close button
   *  included — takes the .tech size (48 px targets, 14 px text). The sheet's
   *  own content wrapping itself in .tech left the Close at 44 px (R2 e2e F2). */
  tech?: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-surface-inverse/40" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-stretch justify-center sm:items-center sm:p-4">
          <Dialog.Panel data-testid={testid}
                        className={`flex min-h-full w-full min-w-0 flex-col bg-surface shadow-xl sm:min-h-0 sm:rounded-2xl ${
                          wide ? 'sm:max-w-3xl' : 'sm:max-w-lg'}${tech ? ' tech' : ''}`}>
            <div className="flex items-start gap-3 border-b border-line-subtle px-4 py-3 sm:px-6 sm:py-4">
              <div className="min-w-0 flex-1">
                <Dialog.Title as="h2" className="break-words text-base font-semibold text-content">{title}</Dialog.Title>
                {subtitle && <p className="mt-0.5 break-words text-xs text-content-muted">{subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Close"
                      className={`-mr-2 inline-flex ${tech ? 'h-12 w-12' : 'h-11 w-11'} shrink-0 items-center justify-center rounded-lg text-content-tertiary hover:bg-surface-muted`}>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="min-w-0 flex-1 space-y-4 px-4 py-4 sm:px-6">{children}</div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** navigator.onLine, kept current. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export const OFFLINE = "You're offline";

/** Every FM ticket write re-reads every FM screen it can change; bound to
 *  the ticket (if any) so a call site reads `invalidate()`. */
export function useFmInvalidate(ticketId?: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['fm', 'tickets'] });
    if (ticketId) void qc.invalidateQueries({ queryKey: ['fm', 'ticket', ticketId] });
    void qc.invalidateQueries({ queryKey: ['fm', 'home'] });
    void qc.invalidateQueries({ queryKey: ['fm', 'shell'] });
    void qc.invalidateQueries({ queryKey: ['fm', 'inbox'] });
  };
}

/** The API's own words for a 409 / 422 (they are written for people);
 *  anything else is said plainly, never as raw error text. */
export function actionError(err: unknown, fallback = "Couldn't do that. Try again."): string {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  if (status === 409 || status === 422) {
    return routeRequestError(err, {})?.message ?? fallback;
  }
  return fallback;
}

export function httpStatusOf(err: unknown): number {
  return (err as { response?: { status?: number } } | null)?.response?.status ?? 0;
}

export function LoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="card flex items-center justify-between gap-3 p-4" role="alert">
      <span className="text-sm text-content-secondary">Couldn't load this.</span>
      <button type="button" className="btn-secondary min-h-[44px]" onClick={onRetry}>Retry</button>
    </div>
  );
}
